import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useThunk } from "../../hooks/useThunk";
import useForm from "../../hooks/useForm";
import {
  getRemixerProfiles,
  createRemixerUser,
  updateRemixerUser,
  getRemixerUserById,
} from "../../services/thunks/remixerUserThunks";
import showToast from "../../utils/toastNotifications";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";

import Input from "../../components/Form/Input";
import { formStyles } from "../../components/Form/FormUtils";
import Dropdown from "../../components/Form/Dropdown";
import CustomButton from "../../components/Buttons/CustomButton";


const AddNewRemixerUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);

  const userId = id;
  const isEditMode = Boolean(userId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [remixerProfiles, setRemixerProfiles] = useState([]);
  const [remixerName, setRemixerName] = useState("");

  // Initialize useForm hook
  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
    clearError,
  } = useForm(
    {
      remixerProfileId: "",
      email: "",
      contactNumber: "",
      payoutRate: "",
    },
    {
      remixerProfileId: true,
      email: true,
      payoutRate: true,
    },
    {
      remixerProfileId: COMMON_FIELD_TYPES.REQUIRED_FIELD,
      email: COMMON_FIELD_TYPES.EMAIL_FIELD,
      contactNumber: COMMON_FIELD_TYPES.PHONE,
      payoutRate: COMMON_FIELD_TYPES.POSITIVE_DECIMAL,
    }
  );

  const [doGetRemixerProfiles] = useThunk(getRemixerProfiles);
  const [doCreateRemixerUser] = useThunk(createRemixerUser);
  const [doUpdateRemixerUser] = useThunk(updateRemixerUser);
  const [doGetRemixerUserById] = useThunk(getRemixerUserById);

  useEffect(() => {
    initializeForm();
  }, [userId]);

  const initializeForm = async () => {
    try {
      setLoading(true);

      const profilesResult = await doGetRemixerProfiles({ token: auth.token });

      if (profilesResult.success) {
        setRemixerProfiles(profilesResult.response.data);

        if (isEditMode) {
          const userResult = await doGetRemixerUserById({
            token: auth.token,
            id: userId,
          });

          if (userResult.success) {
            const userData = userResult.response.data;
            console.log("User data loaded:", userData);

            setFormData({
              remixerProfileId:
                userData.remixerProfileId || userData.remixerProfile?._id || "",
              email: userData.email || "",
              contactNumber: userData.contactNumber || "",
              payoutRate: userData.payoutRate?.toString() || "",
            });
            setRemixerName(userData.remixerName || "");
          } else {
            showToast("error", "Failed to load user data");
          }
        } else {
          setFormData({
            remixerProfileId: "",
            email: "",
            contactNumber: "",
            payoutRate: "",
          });

          setTouched({
            remixerProfileId: false,
            email: false,
            contactNumber: false,
            payoutRate: false,
          });
        }
      } else {
        showToast("error", "Failed to load remixer profiles");
      }
    } catch (error) {
      showToast("error", "Failed to load form data");
    } finally {
      setLoading(false);
    }
  };

  const validateFormFields = () => {
    setTouched({
      remixerProfileId: true,
      email: true,
      payoutRate: true,
    });

    if (
      formData.payoutRate &&
      (isNaN(formData.payoutRate) || parseFloat(formData.payoutRate) < 0)
    ) {
      return false;
    }

    const requiredFields = ["remixerProfileId", "email", "payoutRate"];
    const requiredFieldsValid = requiredFields.every((field) => {
      const isValid = formData[field] && !errors[field];

      return isValid;
    });

    return requiredFieldsValid;
  };

  const handleDropdownSelect = (selectedOption) => {
    if (selectedOption) {
      const profileId = selectedOption._id;
      handleInputChange("remixerProfileId", profileId);
    } else {
      handleInputChange("remixerProfileId", "");
    }
  };

  const handleSave = async () => {
    if (!validateForm() || !validateFormFields() ) {
      showToast("error", "Please fill all required fields correctly");
      console.log("Form validation failed:", { formData, errors });
      console.log("Touched fields:", touched);
      return;
    }

    try {
      setSaving(true);
      const selectedProfile = remixerProfiles.find(
        (profile) => profile._id === formData.remixerProfileId
      );

      const saveData = {
        name: selectedProfile?.remixerName || formData.remixerProfileId,
        email: formData.email,
        contactNumber: formData.contactNumber || "",
        payoutRate: parseFloat(formData.payoutRate),
        remixerProfileId: formData.remixerProfileId,
      };

      let result;
      if (isEditMode) {
        result = await doUpdateRemixerUser({
          token: auth.token,
          id: userId,
          ...saveData,
        });
      } else {
        result = await doCreateRemixerUser({
          token: auth.token,
          payload: saveData,
        });
      }

      if (result.success) {
        showToast(
          "success",
          `Remixer user ${isEditMode ? "updated" : "created"} successfully`
        );
        navigate("/remixer-overview");
      } else {
        showToast(
          "error",
          result.error?.message ||
            `Failed to ${isEditMode ? "update" : "create"} remixer user`
        );
      }
    } catch (error) {
      showToast(
        "error",
        `An error occurred while ${
          isEditMode ? "updating" : "creating"
        } remixer user`
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

 
  const isFormValid = () => {
    const hasRequiredFields = Boolean(
      formData.remixerProfileId && formData.email && formData.payoutRate
    );

    const touchedFields = Object.keys(touched).filter(
      (key) => touched[key] && formData[key] !== ""
    );

    console.log("Form validation check:", {
      hasRequiredFields,
      formData,
      errors,
      touchedFields,
    });
    
    if (touchedFields.length === 0 && isEditMode) {

      return false; // If no fields touched in edit mode, assume valid
    }
    const hasNoErrors = Object.keys(errors).every((key) => !errors[key]);
    console.log("Form validation:", { hasRequiredFields, hasNoErrors });

    return hasRequiredFields && hasNoErrors;
  };

  const getCurrentSelectedOption = () => {
    if (!formData.remixerProfileId) return null;

    return remixerProfiles.find(
      (profile) => profile._id === formData.remixerProfileId
    );
  };

  if (loading) {
    return (
      <div className="px-6 py-10 text-white min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="px-6 py-10 text-white min-h-screen">
      {/* Remixer User Info Form */}
      <div className="bg-[#111111D6] rounded-lg p-6 border border-[#505050]">
        <h2 className="text-[20px] font-bold font-public-sans mb-6 text-white">
          {isEditMode ? "Edit Remixer User Info" : "Remixer User Info"}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {/* Select Remixer Profile */}
          <div className="md:col-span-1">
          {isEditMode ? (
            <Input
              label="Remixer Name"
              value={formData.remixerName}
              placeholder={remixerName || "Enter remixer name"}
              inputStyle={formStyles.customerApplicationInput + " cursor-not-allowed"}
  
              labelStyle={formStyles.customerApplicationlabel}
              isRequired={true}
              readOnly={true}
              errorContainerStyle="h-4"
            />
          ) : (
            <Dropdown
              label="Select Remixer Profile"
              isRequired={true}
              defaultOption={getCurrentSelectedOption()}
              onSelect={handleDropdownSelect}
              options={remixerProfiles.map((profile) => ({
                _id: profile._id,
                name: profile.remixerName,
                isAssigned: profile.isAssigned,
              }))}
              placeholder="Select Remixer Profile"
              disabled={isEditMode}
              error={touched.remixerProfileId && errors.remixerProfileId}
              labelStyle={formStyles.customerApplicationlabel}
              isSearchable={true}
              name="remixerProfileId"
              displayKey="name"
              idKey="_id"
              allowClear={!isEditMode}
              optionClassName="bg-[#81818180] border border-[#81818180] h-[45px]"
              searchPlaceholder="Search Remixer Profiles"
              outerContainerStyle="gap-0"
              wrapperClassName="h-[45px]"
              placeholderClassName=" font-normal text-[.9375rem] font-public-sans"
              errorContainerStyle="h-4"
              onFocus={() => {
                clearError("remixerProfileId");
                setTouched({ remixerProfileId: true });
              }}
            />
          )}
            {formData.remixerProfileId && !isEditMode && (
              <p className="text-[#FFB800] text-sm mt-1 font-public-sans">
                ⚠️ This Remixer assignment is permanent and cannot be changed
                later.
              </p>
            )}
          </div>

          {/* Email Address */}
          <div className="md:col-span-1">
            <Input
              label="Email Address"
              type="email"
              required={true}
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              onBlur={() => handleBlur("email", formData.email)}
              onFocus={() => {
                clearError("email");
                setTouched({ email: true });
              }}
              placeholder="Enter email address"
              error={touched.email && errors.email}
              inputStyle={formStyles.customerApplicationInput + (isEditMode ? " cursor-not-allowed" : "")}
              labelStyle={formStyles.customerApplicationlabel}
              isRequired={true}
              readOnly={isEditMode}
              errorContainerStyle="h-4"

            />
          </div>

          {/* Contact Number */}
          <div className="md:col-span-1">
            <Input
              label="Contact Number"
              type="tel"
              value={formData.contactNumber}
              onChange={(e) => {
                handleInputChange("contactNumber", e.target.value);
              }}
              onBlur={() => {
                handleBlur("contactNumber", formData.contactNumber);
              }}
              onFocus={() => {
                clearError("contactNumber");
                setTouched({ contactNumber: true });
              }}
              placeholder="Enter contact number"
              error={touched.contactNumber && errors.contactNumber}
              inputStyle={formStyles.customerApplicationInput}
              labelStyle={formStyles.customerApplicationlabel}
              errorContainerStyle="h-4"
            />
          </div>

          {/* Payout Rate */}
          <div className="md:col-span-1">
            <Input
              label="Payout Rate (USD)"
              type="number"
              step="0.01"
              min="0"
              required={true}
              value={formData.payoutRate}
              onChange={(e) => handleInputChange("payoutRate", e.target.value)}
              onBlur={() => handleBlur("payoutRate", formData.payoutRate)}
              onFocus={() => {
                clearError("payoutRate");
                setTouched({ payoutRate: true });
              }}
              placeholder="Enter payout rate"
              error={touched.payoutRate && errors.payoutRate}
              inputStyle={formStyles.customerApplicationInput}
              labelStyle={formStyles.customerApplicationlabel}
              isRequired={true}
              errorContainerStyle="h-4"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-center gap-4 mt-8">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2 bg-transparent border border-[#505050] text-white rounded-md hover:bg-[#505050] transition-colors font-public-sans"
            disabled={saving}
          >
            Cancel
          </button>

          <CustomButton
            type="button"
            onClick={handleSave}
            disabled={!isFormValid() || saving}
            loading={saving}
            className={`text-[18px]  rounded-md font-public-sans transition-colors h-[45px] ${
              isFormValid() && !saving ? "" : " cursor-not-allowed"
            }
            ${isEditMode ? "w-[170px]" : "w-[150px]"}`}
          >
            {saving ? (
              <span >Saving...</span>
            ) : (
              <span>{isEditMode ? "Update Remixer" : "Save Remixer"}</span>
            )}
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default AddNewRemixerUser;
